import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ConsignmentBatchCommand, Shipment, ShipmentSearch, ShipmentStatusCommand } from '../../models/shipment/shipment';
import { S3Object } from '../../models/invoice/invoice';

import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';

export enum uploadTypeEnum {
  files,
  duca,
  packingList,
}

@Injectable({
  providedIn: 'root',
})
export class ShipmentProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.orderApi + '/api/v1/shipment-order';

  public getShipments(): Observable<ApiResponse<Shipment[]>> {
    return this.httpClient.get<ApiResponse<Shipment[]>>(`${this.baseUrl}/all`);
  }

  public getShipment(id: string): Observable<ApiResponse<Shipment>> {
    return this.httpClient.get<ApiResponse<Shipment>>(`${this.baseUrl}/${id}`);
  }

  public createShipment(data: Shipment): Observable<ApiResponse<Shipment>> {
    return this.httpClient.post<ApiResponse<Shipment>>(`${this.baseUrl}`, data);
  }

  public updateShipment(data: Shipment): Observable<Shipment> {
    return this.httpClient.put<Shipment>(`${this.baseUrl}/${data.id}`, data);
  }

  public updateShipmentStatus(command: ShipmentStatusCommand, id: string): Observable<ApiResponse<Shipment>> {
    return this.httpClient.patch<ApiResponse<Shipment>>(`${this.baseUrl}/${id}/status`, command);
  }

  public searchShipment(
    search: ShipmentSearch,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder?: number,
    materializeLots = false,
  ): Observable<ApiResponse<PaginatedApiResponse<Shipment>>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('materializeLots', materializeLots.toString());
    if (sortField) params = params.set('sortField', sortField);
    if (sortOrder !== undefined) params = params.set('sortOrder', sortOrder.toString());
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<Shipment>>>(`${this.baseUrl}/search`, search, { params });
  }

  public uploadFiles(files: File[], invoiceID: string, uploadType: 'Files' | 'PakingList' | 'Duca'): Observable<S3Object[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file, file.name));

    const params = new HttpParams().set('shipmentUploadType', uploadType);

    return this.httpClient
      .post<{ success: boolean; data: S3Object[]; MessageError: string }>(`${this.baseUrl}/upload-files/${invoiceID}`, formData, { params })
      .pipe(map(({ data }) => data));
  }

  public deleteFiles(files: { name: string; presignedUrl: string }[], shipmentID: string, fileType: string) {
    const params = new HttpParams().set('shipmentUploadType', fileType);

    return this.httpClient.post(`${this.baseUrl}/delete-files/${shipmentID}`, files, {
      responseType: 'text',
      params,
    });
  }

  public batchShipmentConsignment(command: ConsignmentBatchCommand): Observable<true> {
    return this.httpClient.post<true>(`${this.baseUrl}/batch-consignment`, command);
  }
}
